/**
 * Schema migration infrastructure for stage layout data
 * Handles version management and data migrations
 */

import { StageLayout, StageObject } from './types';
import { validateStageLayout } from './validation';

// ============================================================================
// Rotation normalization
// ============================================================================

/**
 * Earlier builds stored object yaw in two incompatible ways: the 2D tools used
 * `rotation.z` in DEGREES, while the old Properties-panel "Rotation Y" field
 * used `rotation.y` in RADIANS. As a result, rotating in one view never showed
 * in the other.
 *
 * Canonical representation is now `rotation.z` in degrees (used by the 2D
 * renderer, hit-test, rotate tool, 3D scene, and Properties panel alike).
 *
 * This fold is idempotent: once `rotation.y` is zero it does nothing, and
 * nothing in the current app writes a non-zero `rotation.y`, so it only ever
 * fires on legacy data.
 *
 * The 3D scene renders yaw as `-(rotation.z in radians)` about the Three.js Y
 * axis. The legacy Properties panel applied `rotation.y` (radians) directly to
 * that same axis, so to keep a legacy object's 3D appearance we set
 * `rotation.z += -radiansToDegrees(rotation.y)`.
 */
export function normalizeObjectRotation(obj: StageObject): StageObject {
  const y = obj.rotation?.y || 0;
  if (y === 0) return obj;

  const zDeg = (obj.rotation?.z || 0) + -(y * 180) / Math.PI;
  const normalized = ((zDeg % 360) + 360) % 360;

  return {
    ...obj,
    rotation: { x: 0, y: 0, z: normalized },
  };
}

/**
 * Apply rotation normalization to every object in a layout. Returns the same
 * reference when nothing changed so callers can skip redundant state updates.
 */
export function normalizeLayoutRotations(layout: StageLayout): StageLayout {
  if (!layout?.objects?.length) return layout;

  let changed = false;
  const objects = layout.objects.map((obj) => {
    const next = normalizeObjectRotation(obj);
    if (next !== obj) changed = true;
    return next;
  });

  return changed ? { ...layout, objects } : layout;
}

// ============================================================================
// Version Management
// ============================================================================

/**
 * Current version of the stage layout schema
 * Increment when making breaking changes to the schema
 */
export const CURRENT_VERSION = '1.0.0';

/**
 * Semantic version comparison
 * Returns: -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
function compareVersions(version1: string, version2: string): number {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;

    if (v1 < v2) return -1;
    if (v1 > v2) return 1;
  }

  return 0;
}

// ============================================================================
// Migration Functions
// ============================================================================

/**
 * Type for migration functions
 * Takes data of unknown version and returns StageLayout
 */
type MigrationFunction = (data: unknown) => StageLayout;

/**
 * Registry of all migrations
 * Each migration handles upgrading from one version to the next
 */
const MIGRATIONS: Record<string, MigrationFunction> = {
  // Example migration pattern for future versions:
  // '1.0.0': migrateFrom1_0_0To1_1_0,
  // '1.1.0': migrateFrom1_1_0To2_0_0,
};

/**
 * Migrate data from an older version to the current version
 * @param data - The data to migrate (of unknown version)
 * @returns The migrated data as a StageLayout
 * @throws Error if migration fails or version is invalid
 */
export function migrate(data: unknown): StageLayout {
  // Handle invalid input
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid stage data: must be an object');
  }

  const dataObj = data as Record<string, unknown>;

  // Get version from data
  const dataVersion = dataObj.version;

  if (typeof dataVersion !== 'string') {
    throw new Error(
      'Invalid stage data: missing or invalid version property. Expected a semantic version string (e.g., "1.0.0")'
    );
  }

  // Check if already at current version
  if (dataVersion === CURRENT_VERSION) {
    // Validate and return
    return validateStageLayout(data);
  }

  // Check if version is newer than current (unsupported)
  if (compareVersions(dataVersion, CURRENT_VERSION) > 0) {
    throw new Error(
      `Cannot migrate data from version ${dataVersion}. Current supported version is ${CURRENT_VERSION}. ` +
        'Please upgrade your application.'
    );
  }

  // Check if version is older and has migration path
  if (compareVersions(dataVersion, CURRENT_VERSION) < 0) {
    // Apply migrations in sequence
    let migrated = data;
    let currentVersion = dataVersion;

    const migrationPath = buildMigrationPath(dataVersion);

    if (migrationPath.length === 0) {
      throw new Error(
        `No migration path available from version ${dataVersion} to ${CURRENT_VERSION}. ` +
          'Please use a newer version of your data.'
      );
    }

    // Apply each migration in sequence
    for (const targetVersion of migrationPath) {
      const migration = MIGRATIONS[currentVersion];

      if (!migration) {
        throw new Error(
          `Missing migration from version ${currentVersion} to ${targetVersion}`
        );
      }

      try {
        migrated = migration(migrated);
        currentVersion = targetVersion;
      } catch (error) {
        throw new Error(
          `Migration from ${currentVersion} to ${targetVersion} failed: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    // Final validation
    return validateStageLayout(migrated);
  }

  // Should not reach here
  throw new Error(`Unknown version comparison result for version ${dataVersion}`);
}

/**
 * Safely migrate data, catching and returning errors
 * @param data - The data to migrate (of unknown version)
 * @returns A result object with either migrated data or an error
 */
export function safeMigrate(
  data: unknown
): { success: true; data: StageLayout } | { success: false; error: Error } {
  try {
    const result = migrate(data);
    return { success: true, data: result };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    return { success: false, error: err };
  }
}

/**
 * Build a migration path from a source version to the current version
 * Returns an array of versions in order to migrate through
 * @param sourceVersion - The starting version
 * @returns Array of target versions to migrate to, in order
 */
function buildMigrationPath(sourceVersion: string): string[] {
  // For 1.0.0, return empty array since no migrations needed yet
  // As we add migrations, we would build a path through them
  // Example: ['1.1.0', '2.0.0'] to go from 1.0.0 -> 1.1.0 -> 2.0.0

  if (sourceVersion === CURRENT_VERSION) {
    return [];
  }

  // For now, we only support 1.0.0 as the source version
  if (sourceVersion !== '1.0.0') {
    return [];
  }

  // Return any migrations from 1.0.0 to current
  // This would be populated as we add migrations
  return [];
}

// ============================================================================
// Example Migration Function (for reference)
// ============================================================================

/**
 * Example migration from 1.0.0 to 1.1.0
 * This is a template for future migrations.
 * Uncomment and modify when needed.
 *
 * Changes in 1.1.0:
 * - Added new optional field to objects
 * - Renamed some properties
 *
 */
/*
function migrateFrom1_0_0To1_1_0(data: any): StageLayout {
  // Clone the data to avoid mutating input
  const migrated = JSON.parse(JSON.stringify(data));

  // Update version
  migrated.version = '1.1.0';

  // Example transformations:
  // migrated.objects.forEach((obj: any) => {
  //   // Add new default field if missing
  //   if (!obj.hasOwnProperty('new_field')) {
  //     obj.new_field = 'default_value';
  //   }
  //   // Rename old field to new field
  //   if (obj.old_field_name) {
  //     obj.new_field_name = obj.old_field_name;
  //     delete obj.old_field_name;
  //   }
  // });

  return migrated;
}
*/

// ============================================================================
// Version Information
// ============================================================================

export interface VersionInfo {
  current: string;
  supported: string[];
  canMigrateFrom: string[];
}

/**
 * Get information about supported versions
 * @returns Version information
 */
export function getVersionInfo(): VersionInfo {
  return {
    current: CURRENT_VERSION,
    supported: [CURRENT_VERSION, ...Object.keys(MIGRATIONS)],
    canMigrateFrom: Object.keys(MIGRATIONS),
  };
}

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Check if data appears to be a valid stage layout without full validation
 * Useful for quick checks before migration
 * @param data - The data to check
 * @returns true if data looks like a stage layout, false otherwise
 */
export function isLikelyStageLayout(data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const obj = data as Record<string, unknown>;

  // Check for required top-level fields
  const hasVersion = typeof obj.version === 'string';
  const hasCanvas = !!(obj.canvas && typeof obj.canvas === 'object');
  const hasObjects = Array.isArray(obj.objects);
  const hasPositions = Array.isArray(obj.shooting_positions);
  const hasMetadata = !!(obj.metadata && typeof obj.metadata === 'object');

  return hasVersion && hasCanvas && hasObjects && hasPositions && hasMetadata;
}

/**
 * Get a version from data without full parsing
 * @param data - The data to extract version from
 * @returns The version string or null if not found
 */
export function getDataVersion(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const version = (data as Record<string, unknown>).version;
  return typeof version === 'string' ? version : null;
}

/**
 * Validate that a version string is semantically valid
 * @param version - The version string to validate
 * @returns true if valid, false otherwise
 */
export function isValidVersionString(version: string): boolean {
  // Simple semantic versioning check: X.Y.Z or X.Y
  const semverRegex = /^\d+\.\d+(\.\d+)?$/;
  return semverRegex.test(version);
}
